import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext'; // Added import for useOrganization
import { 
  logRoleVerification, 
  logMembershipVerification, 
  logCachePerformance
} from '../utils/securityMonitoring';

// Cache configuration
const ROLE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MEMBERSHIP_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

// Cache storage
const roleCache = new Map();
const membershipCache = new Map();

export const useSecurityVerification = (organizationId) => {
  const { currentUser } = useAuth();
  const { userRole } = useOrganization(); // Get user role from organization context
  const [isRoleVerified, setIsRoleVerified] = useState(false);
  const [isMembershipVerified, setIsMembershipVerified] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  
  // Cache cleanup interval
  const cleanupIntervalRef = useRef(null);

  // Cleanup expired cache entries
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    
    // Clean role cache
    for (const [key, value] of roleCache.entries()) {
      if (now - value.timestamp > ROLE_CACHE_TTL) {
        roleCache.delete(key);
      }
    }
    
    // Clean membership cache
    for (const [key, value] of membershipCache.entries()) {
      if (now - value.timestamp > MEMBERSHIP_CACHE_TTL) {
        membershipCache.delete(key);
      }
    }
  }, []);

  // Verify user role with caching
  const verifyUserRole = useCallback(async (requiredRole) => {
    if (!currentUser || !organizationId || !requiredRole) {
      return false;
    }

    // Create a consistent cache key for both single roles and arrays
    let cacheKey;
    if (Array.isArray(requiredRole)) {
      // Sort the array to ensure consistent cache keys regardless of order
      const sortedRoles = [...requiredRole].sort();
      cacheKey = `${currentUser.uid}_${organizationId}_${sortedRoles.join('|')}`;
    } else {
      cacheKey = `${currentUser.uid}_${organizationId}_${requiredRole}`;
    }

    const cached = roleCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < ROLE_CACHE_TTL) {
      // Log cache hit
      logCachePerformance('role', true, cacheKey, 0);
      return cached.isVerified;
    }

    // Log cache miss
    logCachePerformance('role', false, cacheKey, 0);

    try {
      setVerificationLoading(true);
      setVerificationError(null);
      
      const startTime = performance.now();
      
      const functions = getFunctions();
      const verifyUserRole = httpsCallable(functions, 'verifyUserRole');
      
      
      const result = await verifyUserRole({ organizationId, requiredRole });
      const isVerified = result.data.isVerified;
      
      const endTime = performance.now();
      
      // Log role verification attempt
      logRoleVerification(currentUser.uid, organizationId, requiredRole, isVerified);
      
      // Cache the result
      roleCache.set(cacheKey, {
        isVerified,
        timestamp: Date.now()
      });
      
      setIsRoleVerified(isVerified);
      return isVerified;
    } catch (error) {
      setVerificationError(error.message);
      
      // Log failed role verification
      logRoleVerification(currentUser.uid, organizationId, requiredRole, false, error);
      
      // Cache negative result to prevent repeated failed calls
      roleCache.set(cacheKey, {
        isVerified: false,
        timestamp: Date.now(),
        error: error.message
      });
      
      setIsRoleVerified(false);
      return false;
    } finally {
      setVerificationLoading(false);
    }
  }, [currentUser, organizationId]);

  // Verify organization membership with caching
  const verifyOrganizationMembership = useCallback(async () => {
    if (!currentUser || !organizationId) {
      return false;
    }

    const cacheKey = `${currentUser.uid}_${organizationId}`;
    const cached = membershipCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < MEMBERSHIP_CACHE_TTL) {
      // Log cache hit
      logCachePerformance('membership', true, cacheKey, 0);
      return cached.isMember;
    }

    // Log cache miss
    logCachePerformance('membership', false, cacheKey, 0);

    try {
      setVerificationLoading(true);
      setVerificationError(null);
      
      const startTime = performance.now();
      
      const functions = getFunctions();
      const verifyMembership = httpsCallable(functions, 'verifyOrganizationMembership');
      
      const result = await verifyMembership({ organizationId });
      const isMember = result.data.isMember;
      
      const endTime = performance.now();
      
      // Log membership verification attempt
      logMembershipVerification(currentUser.uid, organizationId, isMember);
      
      // Cache the result
      membershipCache.set(cacheKey, {
        isMember,
        timestamp: Date.now()
      });
      
      setIsMembershipVerified(isMember);
      return isMember;
    } catch (error) {
      setVerificationError(error.message);
      
      // Log failed membership verification
      logMembershipVerification(currentUser.uid, organizationId, false, error);
      
      // Cache negative result to prevent repeated failed calls
      membershipCache.set(cacheKey, {
        isMember: false,
        timestamp: Date.now(),
        error: error.message
      });
      
      setIsMembershipVerified(false);
      return false;
    } finally {
      setVerificationLoading(false);
    }
  }, [currentUser, organizationId]);

  // Verify both role and membership
  const verifyAccess = useCallback(async (requiredRole) => {
    if (!currentUser || !organizationId) {
      return false;
    }

    try {
      // First verify membership and wait for it to complete
      const isMember = await verifyOrganizationMembership();
      
      if (!isMember) {
        return false;
      }

      // Then verify role if required
      if (requiredRole) {
        // ✅ BUSINESS OWNER BYPASS: BusinessOwner users have access to everything
        if (userRole === 'BusinessOwner') { // Changed from currentUser.role to userRole
          return true;
        }
        
        // Handle both single role strings and arrays of roles
        if (Array.isArray(requiredRole)) {
          // Make a single Firebase call with the array of roles for better performance
          const hasAnyRole = await verifyUserRole(requiredRole);
          return hasAnyRole;
        } else {
          // Single role string (backward compatibility)
          const hasRole = await verifyUserRole(requiredRole);
          return hasRole;
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  }, [currentUser, organizationId, verifyOrganizationMembership, verifyUserRole, userRole]); // Added userRole to dependencies

  // Clear cache for specific user/organization
  const clearCache = useCallback((userId = null, orgId = null) => {
    if (userId && orgId) {
      // Clear specific user/organization cache
      for (const [key] of roleCache.entries()) {
        if (key.startsWith(`${userId}_${orgId}`)) {
          roleCache.delete(key);
        }
      }
      for (const [key] of membershipCache.entries()) {
        if (key.startsWith(`${userId}_${orgId}`)) {
          membershipCache.delete(key);
        }
      }
    } else {
      // Clear all cache
      roleCache.clear();
      membershipCache.clear();
    }
  }, [roleCache, membershipCache]);

  // Force refresh verification data (useful when user data changes)
  const forceRefresh = useCallback(async () => {
    if (currentUser && organizationId) {
      // Clear all cached data for current user/organization
      clearCache(currentUser.uid, organizationId);
      
      // Re-verify membership and role
      await verifyOrganizationMembership();
      setIsRoleVerified(false); // Reset role verification
    }
  }, [currentUser, organizationId, clearCache, verifyOrganizationMembership]);

  // Initialize verification on mount
  useEffect(() => {
    if (currentUser && organizationId) {
      // Clear any existing cache to ensure fresh verification
      clearCache(currentUser.uid, organizationId);
      // Start verification process immediately
      const initializeVerification = async () => {
        try {
          await verifyOrganizationMembership();
        } catch (error) {
          // Silently handle initialization errors
        }
      };
      initializeVerification();
    }
  }, [currentUser, organizationId, verifyOrganizationMembership, clearCache]);

  // Set up cache cleanup interval
  useEffect(() => {
    cleanupIntervalRef.current = setInterval(cleanupCache, 60000); // Clean every minute
    
    return () => {
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
    };
  }, []); // FIXED: Removed cleanupCache from dependencies since it's stable with useCallback

  // Clear cache when user changes
  useEffect(() => {
    if (currentUser) {
      clearCache(currentUser.uid, organizationId);
    }
  }, [currentUser, clearCache, organizationId]);

  return {
    // State
    isRoleVerified,
    isMembershipVerified,
    verificationLoading,
    verificationError,
    
    // Actions
    verifyUserRole,
    verifyOrganizationMembership,
    verifyAccess,
    clearCache,
    forceRefresh,
    
    // Computed
    isFullyVerified: isRoleVerified && isMembershipVerified,
    hasAccess: isMembershipVerified
  };
}; 